import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatDateTime } from "@/lib/api";
import { PageHeader, StatusBadge, EmptyState } from "@/components/app/ui-bits";
import { Plus, UserGear, Key, Prohibit, CheckCircle, Trash, Envelope, BellRinging, Users as UsersIcon, Briefcase, IdentificationCard } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ROLES, ROLE_GROUPS, roleLabel, roleGroup, can } from "@/lib/roles";
import RecordInspector, { useRecordInspector } from "@/components/app/RecordInspector";

const STAFF_ROLES = new Set(["staff", "employee"]);

function RoleDropdown({ value, onChange, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="Select a role" /></SelectTrigger>
      <SelectContent className="max-h-[420px]">
        {ROLE_GROUPS.map((g) => {
          const roles = ROLES.filter((r) => r.group === g);
          if (!roles.length) return null;
          return (
            <SelectGroup key={g}>
              <SelectLabel className="text-[10px] uppercase tracking-widest text-slate-500">{g}</SelectLabel>
              {roles.map((r) => (
                <SelectItem key={r.key} value={r.key} data-testid={`role-opt-${r.key}`}>
                  <div className="flex flex-col">
                    <span>{r.label}</span>
                    <span className="text-[10px] text-slate-500">{r.desc}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectGroup>
          );
        })}
        {/* Legacy support — surface "manager" only when an existing user already
            has it, so admin can re-assign them to a specific role. */}
        {value === "manager" && (
          <SelectGroup>
            <SelectLabel className="text-[10px] uppercase tracking-widest text-amber-700">Legacy</SelectLabel>
            <SelectItem value="manager">Manager (legacy · please re-assign)</SelectItem>
          </SelectGroup>
        )}
      </SelectContent>
    </Select>
  );
}

function BranchFields({ value, onChange }) {
  const [branches, setBranches] = useState([]);
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data.items || [])).catch(() => {});
    api.get("/projects").then((r) => setProjects(Array.isArray(r.data) ? r.data : r.data.items || [])).catch(() => {});
  }, []);
  const set = (k, v) => onChange({ ...value, [k]: v });
  const toggleProject = (pid) => {
    const cur = value.assigned_projects || [];
    set("assigned_projects", cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid]);
  };
  return (
    <div className="space-y-3 rounded-lg border border-blue-200 bg-blue-50/50 p-3" data-testid="branch-fields">
      <div className="overline text-blue-800">Branch assignment</div>
      <div>
        <Label>Branch *</Label>
        <Select value={value.branch_id || ""} onValueChange={(v) => set("branch_id", v)}>
          <SelectTrigger data-testid="branch-select"><SelectValue placeholder="Select the branch" /></SelectTrigger>
          <SelectContent>
            {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} · {b.code}</SelectItem>)}
          </SelectContent>
        </Select>
        {branches.length === 0 && <p className="mt-1 text-[10px] text-amber-700">No branches yet — create one first (admin).</p>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label>Approval limit (USD)</Label>
          <Input type="number" step="0.01" value={value.approval_limit ?? ""} onChange={(e) => set("approval_limit", e.target.value === "" ? null : parseFloat(e.target.value))} data-testid="branch-approval-limit" /></div>
        <div><Label>Effective start date</Label>
          <Input type="date" value={value.effective_start_date || ""} onChange={(e) => set("effective_start_date", e.target.value)} /></div>
      </div>
      <div><Label>Reporting manager (email)</Label>
        <Input value={value.reporting_manager || ""} onChange={(e) => set("reporting_manager", e.target.value)} placeholder="manager@descon.group" /></div>
      <div>
        <Label>Authorized projects (optional — beyond the branch's own)</Label>
        <div className="mt-1 max-h-28 overflow-y-auto rounded border border-slate-200 bg-white p-2 space-y-1">
          {projects.length === 0 ? <div className="text-[10px] text-slate-400">No projects</div> : projects.map((p) => (
            <label key={p.id} className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={(value.assigned_projects || []).includes(p.id)} onChange={() => toggleProject(p.id)} />
              <span>{p.name}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

function UserCreateDialog({ onDone, initial, trigger }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial || { email: "", name: "", password: "", role: "staff" });
  const [busy, setBusy] = useState(false);
  // Reset form when `initial` changes (e.g. switching between rows in Pending Onboarding)
  useEffect(() => {
    if (initial) setForm({ password: "", ...initial });
  }, [initial?.email]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false);
      if (!initial) setForm({ email: "", name: "", password: "", role: "staff" });
      onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || <Button className="rounded-sm" data-testid="add-user-button"><Plus size={14} className="mr-2" /> New user</Button>}
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{initial ? `Create account for ${initial.name || initial.email}` : "Create user"}</DialogTitle>
          <DialogDescription>{initial ? "This employee already exists in HR — set a password and confirm the role to issue a login." : "Password is set once and never displayed. The user can change it after sign-in."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Full name *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" disabled={!!initial?.name} /></div>
          <div><Label>Email *</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" disabled={!!initial?.email} /></div>
          <div><Label>Password * (min 8 chars)</Label><Input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" /></div>
          <div>
            <Label>Role *</Label>
            <RoleDropdown value={form.role} onChange={(v) => setForm({ ...form, role: v })} testid="new-user-role" />
            <p className="text-[10px] text-slate-500 mt-1">{ROLES.find((r) => r.key === form.role)?.desc}</p>
          </div>
          {form.role === "branch_user" && (
            <BranchFields value={form} onChange={(v) => setForm(v)} />
          )}
          <DialogFooter><Button type="submit" disabled={busy} data-testid="create-user-submit">{busy ? "Creating…" : "Create"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ userId, email, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [generated, setGenerated] = useState("");
  const [copied, setCopied] = useState(false);

  const makeTempPassword = () => {
    // 12-char temp password: 1 upper, 1 lower, 1 digit, 1 special, rest mixed
    const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
    const lower = "abcdefghijkmnopqrstuvwxyz";
    const digits = "23456789";
    const special = "@#$%&";
    const all = upper + lower + digits + special;
    const rand = (s) => s[Math.floor(Math.random() * s.length)];
    let pw = [rand(upper), rand(lower), rand(digits), rand(special)];
    for (let i = 0; i < 8; i++) pw.push(rand(all));
    return pw.sort(() => Math.random() - 0.5).join("");
  };

  const submit = async () => {
    setBusy(true);
    const temp = makeTempPassword();
    try {
      await api.post(`/users/${userId}/reset-password`, { new_password: temp });
      setGenerated(temp);
      setCopied(false);
      toast.success("Password reset — share securely");
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generated);
      setCopied(true);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Could not copy — please select and copy manually");
    }
  };

  const close = () => {
    setOpen(false);
    setGenerated("");
    setCopied(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-sm" data-testid={`reset-${userId}`} title="Reset password (auto-generate temp)">
          <Key size={12} />
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Reset password for {email}</DialogTitle>
          <DialogDescription>
            A 12-character temporary password will be generated and shown once.
            Share it through a secure channel — the user should change it on first sign-in.
          </DialogDescription>
        </DialogHeader>

        {!generated ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              ⚠️ The current password will be invalidated immediately. Make sure
              you have the user available to receive the new one.
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={close} data-testid="reset-cancel">Cancel</Button>
              <Button onClick={submit} disabled={busy} className="btn-primary-blue" data-testid="reset-generate-confirm">
                {busy ? "Generating…" : "Generate & reset"}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <div className="overline mb-2 text-emerald-800">Temporary password (shown once)</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 select-all rounded bg-white px-3 py-2 font-mono-alt text-sm font-semibold tracking-wider text-slate-900" data-testid="reset-generated-pw">
                  {generated}
                </code>
                <Button size="sm" variant="outline" onClick={copyToClipboard} data-testid="reset-copy-pw">
                  {copied ? "Copied ✓" : "Copy"}
                </Button>
              </div>
              <div className="mt-3 text-xs text-slate-600">
                Share securely. Closing this dialog will hide the password forever.
              </div>
            </div>
            <DialogFooter>
              <Button onClick={close} className="btn-primary-blue" data-testid="reset-close-done">I have copied it — Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditRoleDialog({ user: u, onDone }) {
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState(u.role);
  const [name, setName] = useState(u.name);
  const [branch, setBranch] = useState({
    branch_id: u.branch_id || "", extra_branch_ids: u.extra_branch_ids || [],
    assigned_projects: u.assigned_projects || [], approval_limit: u.approval_limit ?? null,
    reporting_manager: u.reporting_manager || "", effective_start_date: u.effective_start_date || "",
  });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { role, name };
      if (role === "branch_user") Object.assign(payload, branch);
      await api.patch(`/users/${u.id}`, payload);
      toast.success("Updated"); setOpen(false); onDone?.();
    }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-sm" data-testid={`edit-${u.id}`}>Edit</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Edit user</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div>
            <Label>Role</Label>
            <RoleDropdown value={role} onChange={setRole} testid={`edit-role-${u.id}`} />
            <p className="text-[10px] text-slate-500 mt-1">{ROLES.find((r) => r.key === role)?.desc}</p>
          </div>
          {role === "branch_user" && (
            <BranchFields value={branch} onChange={setBranch} />
          )}
          <DialogFooter><Button type="submit">Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BranchManagerDialog() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", code: "", location: "" });
  const load = () => api.get("/branches").then((r) => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);
  const add = async () => {
    if (!form.name.trim()) return toast.error("Branch name is required");
    try {
      await api.post("/branches", form);
      toast.success("Branch created");
      setForm({ name: "", code: "", location: "" }); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggle = async (b) => {
    try {
      await api.patch(`/branches/${b.id}`, { status: b.status === "active" ? "inactive" : "active" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="manage-branches-btn"><Briefcase size={14} className="mr-2" /> Branches</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="branches-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Branches</DialogTitle>
          <DialogDescription>Operational locations. Branch Users are assigned to exactly one branch and only see its records.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-6 gap-2">
          <Input className="col-span-3" placeholder="Branch name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="branch-name-input" />
          <Input className="col-span-1" placeholder="Code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <Input className="col-span-2" placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
        </div>
        <Button size="sm" onClick={add} className="btn-primary-blue" data-testid="branch-add-btn"><Plus size={12} className="mr-1" /> Add branch</Button>
        <div className="max-h-60 overflow-y-auto divide-y divide-slate-100 rounded border border-slate-200">
          {items.length === 0 ? <div className="p-4 text-center text-xs text-slate-400">No branches yet.</div> : items.map((b) => (
            <div key={b.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`branch-row-${b.code}`}>
              <div>
                <span className="font-semibold text-slate-900">{b.name}</span>
                <span className="ml-2 font-mono-alt text-xs text-slate-500">{b.code}</span>
                <div className="text-[10px] text-slate-500">{b.location}</div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={b.status} />
                <Button size="sm" variant="outline" onClick={() => toggle(b)}>{b.status === "active" ? "Deactivate" : "Activate"}</Button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Users() {
  const { user: me } = useAuth();
  const canManageUsers = can(me, "users.manage") ?? ["admin", "ceo", "super_admin"].includes(me?.role);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState({ count: 0, items: [] });
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "all";
  const setTab = (t) => setSearchParams((sp) => { const p = new URLSearchParams(sp); p.set("tab", t); return p; });
  const inspector = useRecordInspector();

  const load = async () => {
    try {
      const [u, p] = await Promise.all([
        api.get("/users"),
        api.get("/users/staff-pending-account").catch(() => ({ data: { count: 0, items: [] } })),
      ]);
      setUsers(u.data);
      setPending(p.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const suspend = async (id) => {
    try { await api.post(`/users/${id}/suspend`); toast.success("User suspended"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const activate = async (id) => {
    try { await api.post(`/users/${id}/activate`); toast.success("User activated"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("User deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  // Bucket users into Operational vs Employees subsections
  const buckets = useMemo(() => {
    const operational = users.filter((u) => !STAFF_ROLES.has(u.role));
    const employees = users.filter((u) => STAFF_ROLES.has(u.role));
    return { operational, employees };
  }, [users]);

  const visible =
    tab === "operational" ? buckets.operational
    : tab === "employees" ? buckets.employees
    : users;

  const tabs = [
    { v: "all",         l: "All Users",          count: users.length,            icon: UsersIcon },
    { v: "operational", l: "Operational Roles",  count: buckets.operational.length, icon: Briefcase },
    { v: "employees",   l: "Employees / Staff",  count: buckets.employees.length, icon: IdentificationCard },
    { v: "pending",     l: "Pending Onboarding", count: pending.count,           icon: BellRinging, tone: pending.count > 0 ? "amber" : "slate" },
  ];

  return (
    <div data-testid="users-page">
      <PageHeader
        eyebrow="Access Control"
        title="User Management"
        description="Create users, assign roles, reset passwords, suspend, or delete accounts. Super admin privileges required for full control."
        actions={canManageUsers && (
          <div className="flex items-center gap-2">
            <BranchManagerDialog />
            <UserCreateDialog onDone={load} />
          </div>
        )}
      />
      <div className="px-5 md:px-8 pt-3">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200" data-testid="users-tabs">
          {tabs.map((t) => {
            const active = tab === t.v;
            return (
              <button
                key={t.v}
                onClick={() => setTab(t.v)}
                data-testid={`users-tab-${t.v}`}
                className={`relative inline-flex items-center gap-1.5 px-3 py-2 text-sm font-mono-alt uppercase tracking-widest border-b-2 transition ${
                  active ? "border-[#1E5CBA] text-[#1E5CBA]" : "border-transparent text-slate-500 hover:text-slate-900"
                }`}
              >
                <t.icon size={13} weight={active ? "fill" : "regular"} />
                {t.l}
                {t.count > 0 && (
                  <span className={`ml-1 rounded-full px-1.5 py-0 text-[10px] font-bold ${
                    t.tone === "amber" ? "bg-amber-200 text-amber-900" : active ? "bg-[#1E5CBA] text-white" : "bg-slate-200 text-slate-700"
                  }`}>{t.count}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
      <div className="p-5 md:p-8">
        {loading ? (
          <div className="font-mono-alt text-xs uppercase tracking-[0.25em] text-zinc-500">Loading…</div>
        ) : tab === "pending" ? (
          <PendingOnboardingPanel pending={pending} onCreated={load} />
        ) : visible.length === 0 ? (
          <EmptyState icon={UserGear} title="No users in this view" description="Try a different tab or create a new user." />
        ) : (
          <div className="border border-zinc-200 bg-white">
            <table className="data-table w-full">
              <thead>
                <tr>
                  <th>Name</th><th>Email</th><th>Role</th><th>Group</th><th>Status</th>
                  <th>Last login</th><th>Created</th><th className="w-48">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((u) => (
                  <tr
                    key={u.id}
                    className="cursor-pointer hover:bg-slate-50/70 transition-colors"
                    onClick={() => inspector.open(u, { title: u.name || u.email, subtitle: `${roleLabel(u.role)} · ${u.email}` })}
                    data-testid={`user-row-${u.id}`}
                  >
                    <td className="font-semibold">{u.name}{u.id === me.id && <span className="ml-2 stamp text-blue-700 border-blue-700">you</span>}</td>
                    <td><Envelope size={12} className="mr-1 inline" />{u.email}</td>
                    <td className="text-xs">
                      <div className="font-semibold text-slate-900">{roleLabel(u.role)}</div>
                      {u.role === "manager" && (
                        <span className="text-[10px] text-amber-700 font-mono-alt uppercase">legacy · re-assign</span>
                      )}
                    </td>
                    <td className="font-mono-alt text-xs">
                      <span className="stamp text-indigo-700 border-indigo-300 bg-indigo-50">{roleGroup(u.role)}</span>
                    </td>
                    <td><StatusBadge status={u.status} /></td>
                    <td className="font-mono-alt text-xs">{formatDateTime(u.last_login)}</td>
                    <td className="font-mono-alt text-xs">{formatDateTime(u.created_at)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {canManageUsers ? (
                      <div className="flex flex-wrap items-center gap-2">
                        {u.role !== "super_admin" && <EditRoleDialog user={u} onDone={load} />}
                        <ResetPasswordDialog userId={u.id} email={u.email} onDone={load} />
                        {u.role !== "super_admin" && u.id !== me.id && (
                          u.status === "active" ? (
                            <AlertDialog>
                              <AlertDialogTrigger asChild><Button size="sm" variant="outline" className="rounded-sm text-amber-700" data-testid={`suspend-${u.id}`}><Prohibit size={12} /></Button></AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader><AlertDialogTitle>Suspend {u.email}?</AlertDialogTitle><AlertDialogDescription>The user will lose access immediately.</AlertDialogDescription></AlertDialogHeader>
                                <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => suspend(u.id)}>Suspend</AlertDialogAction></AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          ) : (
                            <Button size="sm" variant="outline" className="rounded-sm text-emerald-700" onClick={() => activate(u.id)} data-testid={`activate-${u.id}`}><CheckCircle size={12} /></Button>
                          )
                        )}
                        {u.role !== "super_admin" && u.id !== me.id && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild><Button size="sm" variant="outline" className="rounded-sm text-red-700" data-testid={`delete-${u.id}`}><Trash size={12} /></Button></AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader><AlertDialogTitle>Delete {u.email}?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                              <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(u.id)}>Delete</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                      ) : (
                        <span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-300" data-testid={`readonly-${u.id}`}>view only</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <RecordInspector {...inspector.props} />
    </div>
  );
}

// ── Pending Onboarding panel ──────────────────────────────────────────
function PendingOnboardingPanel({ pending, onCreated }) {
  const inspector = useRecordInspector();
  if (!pending.count) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-5 py-6 text-center" data-testid="pending-onboarding-empty">
        <CheckCircle size={28} weight="fill" className="text-emerald-600 mx-auto mb-2" />
        <div className="font-display text-sm font-semibold text-emerald-900">All employees have user accounts</div>
        <div className="text-xs text-emerald-800 mt-1">When HR adds a new employee, they'll appear here automatically with a "Create account" button.</div>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="pending-onboarding-list">
      <div className="rounded-md border border-amber-200 bg-amber-50/60 px-4 py-3 flex items-start gap-2">
        <BellRinging size={16} weight="fill" className="text-amber-700 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="font-display text-sm font-semibold text-amber-900">
            {pending.count} new employee{pending.count === 1 ? "" : "s"} need a user account
          </div>
          <div className="text-xs text-amber-800 mt-0.5">
            These employees exist in the HR module but cannot log in yet. Create their account and assign a role so they can be assigned tasks and access their workspace.
          </div>
        </div>
      </div>
      <div className="border border-zinc-200 bg-white">
        <table className="data-table w-full">
          <thead>
            <tr>
              <th>Employee</th><th>Email</th><th>Department / Position</th><th>Added</th><th>Suggested role</th><th className="w-48">Action</th>
            </tr>
          </thead>
          <tbody>
            {pending.items.map((s) => (
              <tr
                key={s.id || s.email}
                className="cursor-pointer hover:bg-slate-50/70 transition-colors"
                onClick={() => inspector.open(s, { title: s.name || s.email, subtitle: `${s.department || ""} · ${s.designation || ""}` })}
                data-testid={`pending-row-${s.email}`}
              >
                <td className="font-semibold">{s.name}</td>
                <td><Envelope size={12} className="mr-1 inline" />{s.email}</td>
                <td className="text-xs">
                  <div className="font-semibold">{s.department}</div>
                  <div className="text-slate-500">{s.designation}</div>
                </td>
                <td className="font-mono-alt text-xs">{formatDateTime(s.created_at)}</td>
                <td className="font-mono-alt text-xs">
                  <span className="stamp text-indigo-700 border-indigo-300 bg-indigo-50">{roleLabel(s.suggested_role)}</span>
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <UserCreateDialog
                    onDone={onCreated}
                    initial={{ name: s.name, email: s.email, role: s.suggested_role || "staff", password: "" }}
                    trigger={
                      <Button size="sm" className="rounded-sm" data-testid={`create-account-${s.email}`}>
                        <Plus size={12} className="mr-1" /> Create account
                      </Button>
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <RecordInspector {...inspector.props} />
    </div>
  );
}
